// FluxoCaixaConfig.jsx
import React, { useEffect } from 'react';
import { Input, Select, Typography, Flex, Divider, Switch } from 'antd';
import { useReportBuilder } from '../../../../contexts/ReportBuilderContext';
import AutoResizeOnExpandControl from './configItens/AutoResizeOnExpandControl';
import BudgetScenarioSelect from './configItens/BudgetScenarioSelect';
import { isExcelErp } from '../../utils/builderMetricList';
import { getGeracaoCaixaLabel } from '../../../../helpers/cashflowLabels';

const { Text } = Typography;

export const ERPS_COM_CATEGORIA_SUPERIOR = [0, 1, 2, 3, 5, 7];
const PERIOD_GRANULARITY = {
  ANO: 'ano',
  MES: 'mes',
  SEM: 'semestre',
  TRI: 'trimestre',
  SEMANA: 'semana',
  DIA: 'dia'
};

const FluxoCaixaConfig = () => {
  const { editingWidget, setEditingWidget, sync } = useReportBuilder();

  const erp = sync?.erp;
  const hasCategoriaSuperior = erp !== undefined && ERPS_COM_CATEGORIA_SUPERIOR.includes(erp);
  const excelBlocked = isExcelErp(erp);
  // Bling (6) nunca abre o Fluxo de Caixa por conta bancária — "Conta" nunca é um nível de hierarquia
  // selecionável aqui (nem para os outros ERPs, na verdade: é sempre auto-adicionado pelo widget
  // quando usaSaldo=true), mas para Bling ela também não é auto-adicionada, e o saldo é sempre
  // consolidado (ver isBling em FluxoCaixaWidget.jsx) — os textos de ajuda abaixo refletem isso.
  const isBling = erp === 6;
  
  const optionsDefault = [{ label: 'Categoria', value: 'categoria' }, { label: 'Pessoa', value: 'pessoa' }, { label: 'Dia', value: 'dia' }];
  const optionsDefaultCA = [{label: 'Categoria Superior', value: 'categoriaSuperior'}, ...optionsDefault];
  const options = hasCategoriaSuperior ? optionsDefaultCA : optionsDefault;
  
  const defaultValues = hasCategoriaSuperior
    ? ['categoriaSuperior', 'categoria', 'pessoa', 'dia']
    : ['categoria', 'pessoa', 'dia'];
  
  const config = editingWidget.config || {};
  if (Object.keys(config).length === 0) {
    config.hierarchy = {
      hierarchyOrder: defaultValues
    };
    config.regimeType = 'pagamento';
    config.showBudget = false;
    config.budgetValueType = 'valorMedio';
    config.budgetDisplayMode = 'value';
    config.showBudgetDiff = false;
    config.showBudgetWithoutActuals = true;
    config.usaSaldo = false;
    config.periodGranularity = PERIOD_GRANULARITY.MES;
    config.fillMissingDailyPeriods = true;
    config.analysis = { vertical: false, horizontal: false };
    setEditingWidget({
      ...editingWidget,
      config: config
    });
  }
  const hierarchyConfig = config.hierarchy || {};
  const regimeType = config.regimeType || 'pagamento';
  const showBudget = config.showBudget || false;
  const budgetValueType = config.budgetValueType || 'valorMedio';
  const budgetDisplayMode = config.budgetDisplayMode || 'value';
  const showBudgetDiff = config.showBudgetDiff ?? false;
  const showBudgetWithoutActuals = config.showBudgetWithoutActuals ?? true;
  const usaSaldo = config.usaSaldo || false;
  const periodGranularity = config.periodGranularity || PERIOD_GRANULARITY.MES;
  const fillMissingDailyPeriods = config.fillMissingDailyPeriods ?? true;
  const analysisConfig = config.analysis || {};

  const isFluxoPorDia = periodGranularity === PERIOD_GRANULARITY.DIA;

  useEffect(() => {
    if (!isFluxoPorDia || !config.showBudget) return;
    setEditingWidget((prev) => {
      if (!prev?.config?.showBudget) return prev;
      return {
        ...prev,
        config: {
          ...prev.config,
          showBudget: false
        }
      };
    });
  }, [isFluxoPorDia, config.showBudget, setEditingWidget]);

  useEffect(() => {
    if (!excelBlocked || regimeType !== 'previsao') return;
    setEditingWidget((prev) => ({
      ...prev,
      config: {
        ...(prev?.config || {}),
        regimeType: 'pagamento'
      }
    }));
  }, [excelBlocked, regimeType, setEditingWidget]);

  // Sanitiza hierarchyOrder removendo "conta" (caso exista em config antigo) e limitando a 4 níveis
  const safeHierarchyOrder = (hierarchyConfig.hierarchyOrder || defaultValues)
    .filter(v => v !== 'conta')
    .slice(0, 4);

  const handleRegimeTypeChange = (value) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        regimeType: value
      }
    });
  };

  const handleHierarchyOrderChange = (value) => {
    // Remove "conta" se por acaso vier (não deveria) e limita a 4 níveis
    const sanitizedValue = (value || []).filter(v => v !== 'conta').slice(0, 4);
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        hierarchy: {
          ...hierarchyConfig,
          hierarchyOrder: sanitizedValue
        }
      }
    });
  };

  const handleShowBudgetChange = (checked) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        showBudget: checked
      }
    });
  };

  const handleBudgetValueTypeChange = (value) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        budgetValueType: value
      }
    });
  };

  const handleBudgetDisplayModeChange = (value) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        budgetDisplayMode: value
      }
    });
  };

  const handleShowBudgetDiffChange = (checked) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        showBudgetDiff: checked
      }
    });
  };

  const handleShowBudgetWithoutActualsChange = (checked) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        showBudgetWithoutActuals: checked
      }
    });
  };

  const handleUsaSaldoChange = (checked) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        usaSaldo: checked
      }
    });
  };

  const handlePeriodGranularityChange = (value) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        periodGranularity: value,
        ...(value === PERIOD_GRANULARITY.DIA ? { showBudget: false } : {})
      }
    });
  };

  const handleFillMissingDailyPeriodsChange = (checked) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        fillMissingDailyPeriods: checked
      }
    });
  };

  const handleAnalysisChange = (type, enabled) => {
    setEditingWidget({
      ...editingWidget,
      config: {
        ...config,
        analysis: {
          ...analysisConfig,
          [type]: enabled
        }
      }
    });
  };

  return (
    <Flex vertical gap={16}>
      <div>
        <Text strong>Título</Text>
        <Input
          value={editingWidget.title}
          onChange={e => setEditingWidget({ ...editingWidget, title: e.target.value })}
        />
      </div>

      <div>
        <Text strong>Descrição</Text>
        <Input
          value={editingWidget.description}
          onChange={e => setEditingWidget({ ...editingWidget, description: e.target.value })}
        />
      </div>

      <Divider size='small'/>
      
      <div>
        <Text strong>Regime</Text>
        <Select
          getPopupContainer={(trigger) => trigger.parentNode}
          style={{ width: '100%', marginTop: 4 }}
          value={regimeType}
          onChange={handleRegimeTypeChange}
          options={[
            //{ label: 'Emissão', value: 'emissao' },
            { label: 'Pagamento', value: 'pagamento' },
            { label: 'Vencimento', value: 'vencimento' },
            ...(excelBlocked ? [] : [{ label: 'Previsão', value: 'previsao' }])
          ]}
        />
      </div>

      <div>
        <Text strong>Configuração de Hierarquia</Text>
        <div>
            <Select
              getPopupContainer={(trigger) => trigger.parentNode}
              mode="multiple"
              style={{ width: '100%', marginTop: 4 }}
              value={safeHierarchyOrder}
              onChange={handleHierarchyOrderChange}
              options={options}
              autoClearSearchValue={false}
            />
            <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
              {isBling
                ? 'Defina até 4 níveis. Ex.: Categoria → Pessoa → Dia.'
                : 'Defina até 4 níveis. Ex.: Categoria → Pessoa → Dia. Ao ativar "saldos projetados", "Conta" entra automaticamente como último nível.'}
            </Text>
          </div>
      </div>

      <div>
        <Text strong>Visualização da tabela</Text>
        <Select
          getPopupContainer={(trigger) => trigger.parentNode}
          style={{ width: '100%', marginTop: 4 }}
          value={periodGranularity}
          onChange={handlePeriodGranularityChange}
          options={[
            { label: 'Por Ano', value: PERIOD_GRANULARITY.ANO },
            { label: 'Por Mês', value: PERIOD_GRANULARITY.MES },
            { label: 'Por Semestre', value: PERIOD_GRANULARITY.SEM },
            { label: 'Por Trimestre', value: PERIOD_GRANULARITY.TRI },
            { label: 'Por Semana', value: PERIOD_GRANULARITY.SEMANA },
            { label: 'Por Dia', value: PERIOD_GRANULARITY.DIA }
          ]}
        />
      </div>

      {periodGranularity === PERIOD_GRANULARITY.DIA && (
        <div>
          <Flex justify="space-between" align="center">
            <Text strong>Preencher dias sem movimentação</Text>
            <Switch
              checked={fillMissingDailyPeriods}
              onChange={handleFillMissingDailyPeriodsChange}
            />
          </Flex>
          <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
            Exibe todos os dias do período, preenchendo Entradas, Saídas e Geração com zero quando não houver lançamentos
          </Text>
        </div>
      )}

      {!isFluxoPorDia && (
        <>
          <Divider size='small'/>

          <div>
            <Flex justify="space-between" align="center">
              <Text strong>Exibir planejamento orçamentário</Text>
              <Switch
                checked={showBudget}
                onChange={handleShowBudgetChange}
              />
            </Flex>
            <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
              Exibe valores de orçamento junto com os dados do fluxo de caixa nas visões anual, semestral, trimestral e mensal
            </Text>
          </div>

          {showBudget && (
            <>
              <BudgetScenarioSelect syncId={sync?.id} value={budgetValueType} onChange={handleBudgetValueTypeChange} />

              <div>
                <Text strong>Modo de exibição</Text>
                <Select
                  getPopupContainer={(trigger) => trigger.parentNode}
                  style={{ width: '100%', marginTop: 4 }}
                  value={budgetDisplayMode}
                  onChange={handleBudgetDisplayModeChange}
                  options={[
                    { label: 'Somente valor', value: 'value' },
                    { label: 'Somente percentual', value: 'percent' },
                    { label: 'Valor e percentual', value: 'both' }
                  ]}
                />
                <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
                  Define se o orçamento mostrará valor, percentual ou ambos
                </Text>
              </div>

              <div>
                <Flex justify="space-between" align="center">
                  <Text strong>Exibir diferença (Realizado − Orçado)</Text>
                  <Switch
                    checked={showBudgetDiff}
                    onChange={handleShowBudgetDiffChange}
                  />
                </Flex>
                <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
                  Mostra em R$ quanto o realizado ficou acima ou abaixo do orçado. Verde quando o resultado é melhor que o orçado (entrada acima ou saída abaixo)
                </Text>
              </div>

              <div>
                <Flex justify="space-between" align="center">
                  <Text strong>Exibir orçamento mesmo sem realizado</Text>
                  <Switch
                    checked={showBudgetWithoutActuals}
                    onChange={handleShowBudgetWithoutActualsChange}
                  />
                </Flex>
                <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
                  Exibe o orçamento de períodos e categorias sem realizado, por exemplo um mês futuro ou o plano de um ano que ainda não começou. Só entram categorias da empresa: códigos do cenário que não são categorias do fluxo de caixa não aparecem
                </Text>
              </div>
            </>
          )}
        </>
      )}

      <Divider size='small'/>

      <div>
        <Text strong>Análises</Text>
        <Flex vertical gap={12} style={{ marginTop: 8 }}>
          <Flex justify="space-between" align="center" gap={12}>
            <div>
              <Text>Análise Vertical</Text>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                Representatividade de cada linha de Entradas e Saídas sobre o Total de Entradas do período (Entradas = 100%)
              </Text>
            </div>
            <Switch
              checked={analysisConfig.vertical || false}
              onChange={(checked) => handleAnalysisChange('vertical', checked)}
            />
          </Flex>

          <Flex justify="space-between" align="center" gap={12}>
            <div>
              <Text>Análise Horizontal</Text>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                Compara cada linha de Entradas, Saídas e {getGeracaoCaixaLabel()} com a coluna do período anterior (%)
              </Text>
            </div>
            <Switch
              checked={analysisConfig.horizontal || false}
              onChange={(checked) => handleAnalysisChange('horizontal', checked)}
            />
          </Flex>
        </Flex>
      </div>

      <Divider size='small'/>

      <AutoResizeOnExpandControl editingWidget={editingWidget} setEditingWidget={setEditingWidget} />

      <Divider size='small'/>

      <div>
        <Flex justify="space-between" align="center">
          <Text strong>Exibir saldos projetados</Text>
          <Switch
            checked={usaSaldo}
            onChange={handleUsaSaldoChange}
          />
        </Flex>
        <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
          {isBling
            ? 'Exibe linhas de Saldo Inicial e Saldo Final consolidados (soma de todas as contas bancárias)'
            : 'Exibe linhas de Saldo Inicial e Saldo Final com projeção por conta bancária'}
        </Text>
      </div>
    </Flex>
  );
};

export default FluxoCaixaConfig;
